import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { shortAddr } from "../../lib/format";

export default function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const links = user
        ? [
              { to: "/terminal", label: "Terminal", testid: "nav-terminal" },
              { to: "/positions", label: "Positions", testid: "nav-positions" },
              { to: "/wallet", label: "Wallet", testid: "nav-wallet" },
              { to: "/me/onchain", label: "On-chain", testid: "nav-onchain" },
              { to: "/how-it-works", label: "How BUBE Works", testid: "nav-how-it-works" },
              { to: "/fees", label: "Fees", testid: "nav-fees" },
              ...(user.role === "admin"
                  ? [{ to: "/admin", label: "Admin", testid: "nav-admin" }]
                  : []),
          ]
        : [
              { to: "/onchain", label: "On-chain", testid: "nav-onchain-public" },
              { to: "/how-it-works", label: "How BUBE Works", testid: "nav-how-it-works-public" },
              { to: "/fees", label: "Fees", testid: "nav-fees-public" },
          ];

    const navItem = (l) => (
        <NavLink
            key={l.testid}
            to={l.to}
            data-testid={l.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.18em] font-medium px-3 py-1.5 transition-colors ${
                    isActive ? "text-cyan-400" : "text-zinc-400 hover:text-zinc-50"
                }`
            }
        >
            {l.label}
        </NavLink>
    );

    const mobileNavItem = (l) => (
        <NavLink
            key={l.testid + "-m"}
            to={l.to}
            data-testid={l.testid + "-mobile"}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
                `block text-sm uppercase tracking-[0.18em] font-medium px-2 py-3 border-b border-[#1F1F24] transition-colors ${
                    isActive ? "text-cyan-400" : "text-zinc-300 hover:text-zinc-50"
                }`
            }
        >
            {l.label}
        </NavLink>
    );

    return (
        <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-b border-[#1F1F24]">
            <div className="max-w-[1880px] mx-auto px-3 sm:px-4 h-14 flex items-center justify-between gap-3">
                {/* Brand */}
                <div className="flex items-center gap-4 lg:gap-8 min-w-0">
                    <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 shrink-0">
                        <div className="w-7 h-7 rounded-sm bg-cyan-400 grid place-items-center text-black font-bold font-mono text-sm">V</div>
                        <div className="leading-none">
                            <div className="text-sm font-semibold tracking-tight">Verslan</div>
                            <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-mono mt-0.5">BUBE</div>
                        </div>
                    </Link>
                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {links.map(navItem)}
                    </nav>
                </div>

                {/* Right side */}
                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    <div className="hidden xl:flex items-center gap-2 text-[11px] font-mono text-zinc-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
                        Hyperliquid Mainnet Oracle
                    </div>
                    {user ? (
                        <>
                            <span
                                data-testid="header-wallet-address"
                                className="hidden md:inline-block text-[11px] font-mono text-zinc-400 border border-[#1F1F24] px-2.5 py-1 rounded-sm"
                            >
                                {shortAddr(user.wallet_address)}
                            </span>
                            <button
                                data-testid="logout-button"
                                onClick={async () => { await logout(); navigate("/"); }}
                                className="hidden lg:inline-flex text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-rose-400 px-2.5 py-1.5"
                            >
                                Logout
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/auth"
                                data-testid="nav-login"
                                className="hidden sm:inline-flex text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-zinc-50 px-3 py-1.5"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/auth?mode=register"
                                data-testid="nav-launch"
                                className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-black bg-cyan-400 hover:bg-cyan-300 px-2.5 sm:px-3 py-1.5 rounded-sm font-semibold whitespace-nowrap"
                            >
                                <span className="sm:hidden">Launch</span>
                                <span className="hidden sm:inline">Launch Testnet</span>
                            </Link>
                        </>
                    )}
                    {/* Hamburger */}
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((o) => !o)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        className="lg:hidden inline-flex items-center justify-center w-9 h-9 border border-[#1F1F24] rounded-sm text-zinc-300 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                    >
                        {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="mobile-nav-drawer"
                    className="lg:hidden absolute top-14 left-0 right-0 bg-[#050505]/98 backdrop-blur-xl border-b border-[#1F1F24] shadow-2xl"
                >
                    <nav className="max-w-[1880px] mx-auto px-4 py-3 flex flex-col">
                        {links.map(mobileNavItem)}
                        {user && (
                            <div className="flex items-center justify-between pt-4 pb-2">
                                <span
                                    data-testid="mobile-wallet-address"
                                    className="text-[11px] font-mono text-zinc-400 border border-[#1F1F24] px-2.5 py-1 rounded-sm"
                                >
                                    {shortAddr(user.wallet_address)}
                                </span>
                                <button
                                    data-testid="mobile-logout-button"
                                    onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                                    className="text-[11px] uppercase tracking-[0.18em] text-rose-400 px-3 py-1.5 border border-rose-500/30 rounded-sm"
                                >
                                    Logout
                                </button>
                            </div>
                        )}
                        {!user && (
                            <div className="pt-4 pb-2 flex flex-col gap-2">
                                <Link
                                    to="/auth"
                                    onClick={() => setOpen(false)}
                                    data-testid="mobile-nav-login"
                                    className="text-center text-[11px] uppercase tracking-[0.18em] text-zinc-300 border border-[#1F1F24] px-3 py-2.5 rounded-sm"
                                >
                                    Sign in
                                </Link>
                                <Link
                                    to="/auth?mode=register"
                                    onClick={() => setOpen(false)}
                                    data-testid="mobile-nav-launch"
                                    className="text-center text-[11px] uppercase tracking-[0.18em] text-black bg-cyan-400 px-3 py-2.5 rounded-sm font-semibold"
                                >
                                    Launch Testnet
                                </Link>
                            </div>
                        )}
                        <div className="pt-3 flex items-center gap-2 text-[10px] font-mono text-zinc-500 border-t border-[#1F1F24] mt-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
                            Hyperliquid Mainnet Oracle · live
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}
